import React, { useState, useEffect } from 'react';

interface ToastMessage {
  id: number;
  message: string;
  type: 'success' | 'error';
}

const Toast = ({ message, type, onClose }: { message: string; type: 'success' | 'error'; onClose: () => void }) => {
  useEffect(() => {
    const timer = setTimeout(() => {
      onClose();
    }, 3000);
    return () => clearTimeout(timer);
  }, [onClose]);

  return (
    <div className={`fixed bottom-4 right-4 px-6 py-3 rounded-lg shadow-lg text-white font-medium z-50 animate-slide-in ${
      type === 'success' ? 'bg-green-600' : 'bg-red-600'
    }`}>
      <div className="flex items-center gap-2">
        <span>{type === 'success' ? '✅' : '❌'}</span>
        <span>{message}</span>
        <button 
          onClick={onClose}
          className="ml-2 text-white hover:text-gray-200"
        >
          ×
        </button>
      </div>
    </div>
  );
};

interface Innovation {
  _id: string;
  title: string;
  description: string;
  category: string;
  status: string;
  imageUrl: string;
  technologies: string[];
  link?: string;
  featured: boolean;
}

interface FormData {
  title: string;
  description: string;
}

interface InnovationSectionProps {
  token?: string;
}

const InnovationSection: React.FC<InnovationSectionProps> = ({ token }) => {
  console.log('🚀 InnovationSection component mounted - NEW TABLE DESIGN');
  const [innovations, setInnovations] = useState<Innovation[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editingInnovation, setEditingInnovation] = useState<Innovation | null>(null);
  const [toasts, setToasts] = useState<ToastMessage[]>([]);
  const [formData, setFormData] = useState<FormData>({
    title: '',
    description: ''
  });



  // Toast functions
  const showToast = (message: string, type: 'success' | 'error') => {
    const id = Date.now();
    setToasts(prev => [...prev, { id, message, type }]);
  };

  const removeToast = (id: number) => {
    setToasts(prev => prev.filter(toast => toast.id !== id));
  };

  // Filter innovations based on search term
  const filteredInnovations = innovations.filter(innovation =>
    innovation.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
    innovation.description.toLowerCase().includes(searchTerm.toLowerCase())
  );

  useEffect(() => {
    console.log('🔥 useEffect triggered - fetching innovations');
    fetchInnovations();
  }, []);

  const fetchInnovations = async () => {
    console.log('🔄 Fetching innovations...');
    setLoading(true);
    setError(null);
    
    try {
      const response = await fetch('/api/innovations');
      console.log('📡 Response status:', response.status);
      
      if (response.ok) {
        const result = await response.json();
        console.log('📊 API Response:', result);
        
        // API returns { success: true, data: [...] }
        const innovations = result.success ? result.data : result;
        console.log('✅ Processed innovations:', innovations.length);
        setInnovations(Array.isArray(innovations) ? innovations : []);
      } else {
        console.error('❌ Failed to fetch innovations:', response.status);
        setError(`Failed to fetch innovations: ${response.status}`);
        setInnovations([]);
      }
    } catch (error) {
      console.error('❌ Error fetching innovations:', error);
      setError(error instanceof Error ? error.message : 'Unknown error occurred');
      setInnovations([]);
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (innovation: Innovation) => {
    console.log('✏️ Edit innovation:', innovation.title);
    setEditingInnovation(innovation);
    setFormData({
      title: innovation.title,
      description: innovation.description
    });
    setShowForm(true);
  };

  const handleDelete = async (innovationId: string) => {
    if (!window.confirm('Are you sure you want to delete this innovation?')) {
      return;
    }

    try {
      const response = await fetch(`/api/innovations/${innovationId}`, {
        method: 'DELETE',
        credentials: 'include',
      });

      if (response.ok) {
        console.log('🗑️ Innovation deleted successfully');
        showToast('Innovation deleted successfully!', 'success');
        fetchInnovations(); // Refresh the list
      } else {
        console.error('❌ Failed to delete innovation');
        showToast('Failed to delete innovation', 'error');
      }
    } catch (error) {
      console.error('❌ Error deleting innovation:', error);
      showToast('Error deleting innovation', 'error');
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      const method = editingInnovation ? 'PUT' : 'POST';
      const url = editingInnovation ? `/api/innovations/${editingInnovation._id}` : '/api/innovations';

      const innovationData = {
        title: formData.title,
        description: formData.description,
        // Keep existing values for fields not in form
        category: editingInnovation?.category || 'AI & Machine Learning',
        status: editingInnovation?.status || 'Research',
        imageUrl: editingInnovation?.imageUrl || '',
        technologies: editingInnovation?.technologies || [],
        link: editingInnovation?.link || '',
        featured: editingInnovation?.featured || false
      };

      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify(innovationData),
      });

      if (response.ok) {
        const message = editingInnovation ? 'Innovation updated successfully!' : 'Innovation created successfully!';
        showToast(message, 'success');
        resetForm();
        fetchInnovations();
      } else {
        const errorData = await response.json();
        showToast(errorData.message || 'Failed to save innovation', 'error');
      }
    } catch (error) {
      console.error('Error saving innovation:', error);
      showToast('Error saving innovation', 'error');
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      description: ''
    });
    setEditingInnovation(null);
    setShowForm(false);
  };



  // Safe rendering with error handling
  if (error) {
    return (
      <div className="p-6">
        <h1 className="text-2xl font-bold mb-6">Innovation Management</h1>
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
          <strong>Error:</strong> {error}
          <button 
            onClick={() => fetchInnovations()} 
            className="ml-4 px-3 py-1 bg-red-600 text-white rounded hover:bg-red-700"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="p-6">
        <h1 className="text-2xl font-bold mb-6">Innovation Management</h1>
        <p>Loading innovations...</p>
      </div>
    );
  }

  return (
    <div className="text-gray-100 font-sans h-full" style={{backgroundColor: '#0a0908', padding: '2rem'}}>
      <div className="max-w-7xl mx-auto">
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-4xl font-bold bg-gradient-to-r from-dusk-amber to-dusk-copper bg-clip-text text-transparent mb-2">Innovations</h1>
            <p className="text-gray-400">Manage DUSKPROOF innovation projects and research</p>
          </div>
          <div className="flex items-center space-x-4">
            <div className="relative">
              <input
                type="text"
                placeholder="Search innovations..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 pr-4 py-3 bg-[rgba(243,235,226,0.04)] border border-[var(--dusk-line)] rounded-xl text-white placeholder-[var(--dusk-faint)] focus:outline-none focus:ring-2 focus:ring-dusk-copper/50 focus:border-dusk-copper transition-all"
              />
              <svg 
                className="absolute left-3 top-3.5 h-5 w-5 text-[var(--dusk-faint)]" 
                fill="none" 
                stroke="currentColor" 
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            </div>
            <button
              onClick={() => {
                if (showForm) {
                  resetForm();
                } else {
                  setShowForm(true);
                }
              }}
              className="px-6 py-3 bg-gradient-to-r from-dusk-copper to-dusk-amber hover:from-dusk-copper-deep hover:to-dusk-copper text-white font-semibold rounded-xl transition-all shadow-lg hover:shadow-dusk-copper/40"
            >
              {showForm ? (
                <>
                  <svg className="w-5 h-5 mr-2 inline" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                  Cancel
                </>
              ) : (
                <>
                  <svg className="w-5 h-5 mr-2 inline" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                  </svg>
                  Add Innovation
                </>
              )}
            </button>
          </div>
        </div>

        {/* Innovation Form */}
        {showForm && (
          <form onSubmit={handleSubmit} className="p-8 space-y-6 mb-8 rounded-2xl border border-[var(--dusk-line)] bg-[rgba(243,235,226,0.04)] backdrop-blur-sm">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Title
              </label>
              <input
                type="text"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                className="w-full px-3 py-2 bg-[rgba(243,235,226,0.06)] border border-[var(--dusk-line-strong)] rounded-md text-white"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Description
              </label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                className="w-full px-3 py-2 bg-[rgba(243,235,226,0.06)] border border-[var(--dusk-line-strong)] rounded-md text-white h-24 resize-none"
                placeholder="Describe the innovation..."
                required
              />
            </div>

            <div className="flex space-x-4">
              <button
                type="submit"
                className="px-6 py-2 bg-green-600 hover:bg-green-700 text-white font-medium rounded-lg transition-colors"
              >
                {editingInnovation ? 'Update Innovation' : 'Create Innovation'}
              </button>
              <button
                type="button"
                onClick={resetForm}
                className="px-6 py-2 bg-[rgba(243,235,226,0.06)] hover:bg-gray-600 text-white font-medium rounded-lg transition-colors"
              >
                Cancel
              </button>
            </div>
          </form>
        )}

        {/* Innovations Table */}
        <div className="rounded-2xl border border-[var(--dusk-line)] overflow-hidden bg-[rgba(243,235,226,0.03)] backdrop-blur-sm">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-[rgba(243,235,226,0.05)] border-b border-[var(--dusk-line)]">
                <tr>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-300 uppercase tracking-wider">
                    Innovation
                  </th>
                  <th className="px-6 py-4 text-right text-xs font-semibold text-gray-300 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-700/30">
                {filteredInnovations.length === 0 ? (
                  <tr>
                    <td colSpan={2} className="px-6 py-8 text-center text-gray-400">
                      No data
                    </td>
                  </tr>
                ) : (
                  filteredInnovations.map((innovation) => (
                    <tr key={innovation._id} className="hover:bg-[rgba(243,235,226,0.05)] transition-all">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="flex-shrink-0 h-12 w-12">
                            {innovation.imageUrl && (
                              <img
                                className="h-12 w-12 rounded-lg object-cover"
                                src={innovation.imageUrl}
                                alt={innovation.title}
                                onError={(e) => {
                                  e.currentTarget.style.display = 'none';
                                }}
                              />
                            )}
                          </div>
                          <div className="ml-4">
                            <div className="text-sm font-medium text-white">{innovation.title}</div>
                            <div className="text-sm text-gray-300 max-w-xs truncate">{innovation.description}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <div className="flex items-center justify-end space-x-2">
                          <button
                            onClick={() => handleEdit(innovation)}
                            className="p-2 text-yellow-400 hover:text-yellow-300 hover:bg-yellow-500/20 rounded-lg transition-all"
                            title="Edit"
                          >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                            </svg>
                          </button>
                          <button
                            onClick={() => handleDelete(innovation._id)}
                            className="p-2 text-red-400 hover:text-red-300 hover:bg-red-500/20 rounded-lg transition-all"
                            title="Delete"
                          >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                            </svg>
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        {filteredInnovations.length > 0 && (
          <div className="bg-[rgba(243,235,226,0.04)] px-6 py-3 border-t border-gray-700">
            <div className="text-sm text-gray-400">
              Showing {filteredInnovations.length} of {innovations.length} innovations
            </div>
          </div>
        )}
      </div>

      {/* Toast notifications */}
      <div className="fixed bottom-4 right-4 space-y-2 z-50">
        {toasts.map(toast => (
          <Toast
            key={toast.id}
            message={toast.message}
            type={toast.type}
            onClose={() => removeToast(toast.id)}
          />
        ))}
      </div>
    </div>
  );
};

export default InnovationSection;
